import React, { createContext, useContext, useState, useEffect, useMemo, useCallback } from 'react';
import axios from 'axios';

const API_URL = import.meta.env.VITE_BACKEND_URL + '/api';

const AuthContext = createContext(null);

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const initAuth = async () => {
      if (token) {
        try {
          const response = await axios.get(`${API_URL}/auth/me`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          setUser(response.data);
        } catch (error) {
          localStorage.removeItem('token');
          setToken(null);
          setUser(null);
        }
      }
      setLoading(false);
    };
    initAuth();
  }, [token]);

  const login = async (email, password) => {
    const response = await axios.post(`${API_URL}/auth/login`, { email, password });
    const { access_token, user: userData } = response.data;
    localStorage.setItem('token', access_token);
    setToken(access_token);
    setUser(userData);
    return userData;
  };

  const register = async (email, password, name) => {
    const response = await axios.post(`${API_URL}/auth/register`, { email, password, name });
    const { access_token, user: userData } = response.data;
    localStorage.setItem('token', access_token);
    setToken(access_token);
    setUser(userData);
    return userData;
  };

  const logout = () => {
    localStorage.removeItem('token');
    setToken(null);
    setUser(null);
  };

  // Memoized auth headers — stable reference per token value.
  // FIX (2026-05-25): Previously `getAuthHeaders` returned a fresh object on
  // every render. Consumers that captured it in useCallback/useEffect deps
  // (e.g. BookingDetailPage, IncidentManagementPage in AdminControlTowerV2.js)
  // were re-firing fetches every render, producing HTTP 429 floods and the
  // "Failed to load booking" intermittent error. Memoizing both the headers
  // object and the getter resolves the loop without touching consumer code.
  // The external contract is unchanged: `getAuthHeaders()` still returns
  // `{ Authorization: 'Bearer <token>' }`.
  const authHeaders = useMemo(
    () => ({ Authorization: `Bearer ${token}` }),
    [token]
  );
  const getAuthHeaders = useCallback(() => authHeaders, [authHeaders]);

  return (
    <AuthContext.Provider value={{ user, token, loading, login, register, logout, getAuthHeaders }}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthProvider;
